/**
 * Provider status from each vendor's public Statuspage. All three allow CORS,
 * so no key or proxy is needed.
 *
 * The page-level indicator is deliberately NOT used: Vonage's covers their whole
 * API surface, so an SMS incident reports the page degraded while every video
 * component is operational. Status comes from the components we depend on.
 */

/** Statuspage component states, worst last. */
const STATUS_ORDER = [
	'operational',
	'under_maintenance',
	'degraded_performance',
	'partial_outage',
	'major_outage',
];

export interface StatuspageComponent {
	name: string;
	status: string;
	group?: boolean;
}

export interface StatuspageIncident {
	name: string;
	impact: string;
	shortlink?: string;
	components?: { name: string }[];
}

export interface StatuspageSummary {
	page?: { name?: string; url?: string; updated_at?: string };
	components?: StatuspageComponent[];
	incidents?: StatuspageIncident[];
}

export interface ProviderDefinition {
	key: string;
	label: string;
	url: string;
	componentMatches: string[]; /** Lowercase substrings identifying the components this app depends on. */
}

export const PROVIDERS: ProviderDefinition[] = [
	{
		key: 'vonage',
		label: 'Vonage / OpenTok',
		url: 'https://vonageapi.statuspage.io',
		componentMatches: ['video', 'session connection', 'media connection', 'archiving', 'recordings'],
	},
	{
		key: 'livekit',
		label: 'LiveKit',
		url: 'https://status.livekit.io',
		// Matched by capability, not region.
		componentMatches: ['real time communication', 'egress', 'ingress', 'turn'],
	},
	{
		key: 'mux',
		label: 'Mux',
		url: 'https://status.mux.com',
		componentMatches: ['mux video'],
	},
];

export interface ProviderStatus {
	key: string;
	label: string;
	url: string;
	status: string;
	affected: StatuspageComponent[];
	incidents: StatuspageIncident[];
	componentCount: number;
}

const worstOf = (statuses: string[]): string => statuses.reduce(
	(worst, status) => (STATUS_ORDER.indexOf(status) > STATUS_ORDER.indexOf(worst) ? status : worst),
	'operational',
);

export function summarizeProvider(
	definition: ProviderDefinition,
	summary: StatuspageSummary | null,
): ProviderStatus {
	const base = { key: definition.key, label: definition.label, url: definition.url };

	const relevant = (summary?.components ?? []).filter((component) => (
		!component.group
		&& definition.componentMatches.some((match) => component.name.toLowerCase().includes(match))
	));

	const relevantNames = new Set(relevant.map((component) => component.name));

	return {
		...base,
		// Nothing matched means the page told us nothing: unknown, not healthy.
		status: relevant.length ? worstOf(relevant.map((c) => c.status)) : 'unknown',
		affected: relevant.filter((component) => component.status !== 'operational'),
		incidents: (summary?.incidents ?? []).filter((incident) => (
			incident.components?.some((component) => relevantNames.has(component.name))
		)),
		componentCount: relevant.length,
	};
}

/** Human label for a Statuspage component state. */
export function statusLabel(status: string): string {
	return status.replace(/_/g, ' ');
}

/** True when anything we depend on is not operational. */
export const isProviderDegraded = (provider: ProviderStatus): boolean => (
	provider.status !== 'operational' && provider.status !== 'unknown'
);
